import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Message, SkeletonLoader, spacing } from 'component-library';
import { useTranslation } from 'react-i18next';
import { Country, type SubscriptionType } from '@sr/types';
import {
  countryHasActiveSubscriptionType,
  findCountryFromDwKey,
  getArticleDataBySubscriptionType,
} from '@sr/utils';
import { useProfileV2 } from 'core/lib/Profile';
import { type PartnerInfo } from 'core/lib/types';
import {
  type HandleSelectUserCategoryFn,
  type HierarchyDetailsFromDwKey,
  type SelectHierarchyCallbackFn,
  type SelectHierarchyFn,
  type SelectUserFn,
  type SelectUserIdFn,
  type UserDetailsFromId,
} from '../../types';
import { Divider, SpaceWrapper } from '../../styles';
import useUsersUsers from '../utils/useUsersUsers';
import { BoxContainer } from '../utils/BoxContainer';
import ForMe from './ForMe';
import ForSomeoneElse from './ForSomeoneElse';
import { ORDER_FOR_ME_VALUE } from './constants';

const initialOrderForMeState = true;

const SelectUserAndHierarchy = ({
  onUserChange,
  onHierarchyChange,
  hierarchiesLoading,
  hierarchies,
  hasAvailableCostCenters,
  subscriptionType,
  hasEnterprisePartner,
}: {
  onUserChange: SelectUserFn;
  onHierarchyChange: SelectHierarchyFn;
  hierarchiesLoading: boolean;
  hierarchies?: PartnerInfo[];
  hasAvailableCostCenters: boolean;
  subscriptionType: SubscriptionType;
  hasEnterprisePartner?: boolean;
}) => {
  const { t } = useTranslation();

  const { profile, loading: profileLoading } = useProfileV2();
  const { users, loading: usersLoading } = useUsersUsers({
    requireManageSubscriptions: true,
  });

  const [orderForMe, setOrderForMe] = useState<boolean>(initialOrderForMeState);
  const [selectedUserId, setSelectedUserId] = useState<string>(profile?.userId ?? '');

  const [innerHierarchy, setInnerHierarchy] = useState<string[]>();
  const [selectedUserSubscriptions, setSelectedUserSubscriptions] = useState<string[]>([]);
  const [notAllowedToPurchaseSubscription, setNotAllowedToPurchaseSubscription] =
    useState<boolean>(false);

  const loading = useMemo(
    () => usersLoading || profileLoading || hierarchiesLoading,
    [usersLoading, profileLoading, hierarchiesLoading],
  );

  // Filter users to only show those who have access to at least one partner in the selected country
  const filteredUsers = useMemo(() => {
    if (!users || !hierarchies) return users;

    const availableHierarchyDwKeys = hierarchies.map((h) => h.dwKey);

    // Get all partner IDs from the logged-in user that match the available hierarchies
    const allPartners =
      profile?.permissions?.businessPartners
        .filter((bp) => {
          const country = findCountryFromDwKey(bp.id) ?? Country.SWEDEN;
          const articleData = getArticleDataBySubscriptionType(subscriptionType, country);

          return (
            articleData.active &&
            bp.services.some(
              (s) => s.name === 'services.follow.up.name' || profile.permissions?.superUser,
            )
          );
        })
        .map((obj) => {
          return { id: obj.id, expandedPartners: obj.expandedPartners };
        }) ?? [];

    return users.filter((user) => {
      // Check if user has access to any partner in the available hierarchies
      const hasAccessToCountryPartner = allPartners.some((partner) => {
        // Check if this partner's hierarchy is in the available hierarchies
        if (availableHierarchyDwKeys.includes(partner.id)) {
          // Check if user has direct access to this partner
          if (user.businessPartners.some((userPartner) => userPartner.id === partner.id)) {
            return true;
          }
          // Check if user has access through expanded partners
          if (
            partner.expandedPartners?.some((expandedPartner) =>
              user.businessPartners.some((userPartner) => userPartner.id === expandedPartner),
            )
          ) {
            return true;
          }
        }

        return false;
      });

      return hasAccessToCountryPartner;
    });
  }, [users, hierarchies, profile?.permissions, subscriptionType]);

  const getUserDetailsFromId: UserDetailsFromId = useCallback(
    (id: string) => {
      if (id) {
        if (
          orderForMe &&
          profile?.fullName &&
          profile.email &&
          profile.userId &&
          profile.permissions
        ) {
          return {
            fullName: profile.fullName,
            email: profile.email,
            id,
            phone: profile.phone,
            businessPartners: profile.permissions.businessPartners,
          };
        }

        // If the user is not found, return the first user in the list
        const user = filteredUsers?.find((user) => user.id === id) ?? filteredUsers?.[0];

        if (!user) {
          throw new Error('No user found');
        }

        return {
          fullName: user.fullName,
          email: user.email,
          id: user.id,
          phone: user.phone,
          businessPartners: user.businessPartners,
        };
      }
    },
    [orderForMe, profile, filteredUsers],
  );

  useEffect(() => {
    onUserChange(getUserDetailsFromId(selectedUserId));
  }, [selectedUserId, getUserDetailsFromId, onUserChange]);

  const handleSelectUser: SelectUserIdFn = useCallback(
    (userId?: string) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setSelectedUserId((prevId) => (prevId !== userId ? userId : prevId)!); // userid can be undefined
    },
    [setSelectedUserId],
  );

  const handleSelectHierarchy: SelectHierarchyCallbackFn = useCallback(
    (hierarchy) => {
      setInnerHierarchy(hierarchy);
    },
    [setInnerHierarchy],
  );

  const handleSelectUsersCategory: HandleSelectUserCategoryFn = useCallback(
    (value) => {
      setOrderForMe(value === ORDER_FOR_ME_VALUE);
      if (value === ORDER_FOR_ME_VALUE && profile?.userId) {
        handleSelectUser(profile.userId);
      } else {
        handleSelectUser(undefined);
      }
      handleSelectHierarchy([]);
    },
    [setOrderForMe, profile?.userId, handleSelectHierarchy, handleSelectUser],
  );

  useEffect(() => {
    onHierarchyChange(innerHierarchy?.[0]);
  }, [innerHierarchy, onHierarchyChange]);

  const selectableHierarchies = useMemo(() => {
    if (!selectedUserId) {
      return [];
    }

    const allPartners =
      profile?.permissions?.businessPartners
        .filter((bp) => {
          const country = findCountryFromDwKey(bp.id);
          const businessPartnerCanChooseSubscription =
            country !== undefined && countryHasActiveSubscriptionType(country);

          return (
            businessPartnerCanChooseSubscription &&
            bp.services.some(
              (s) => s.name === 'services.follow.up.name' || profile.permissions?.superUser,
            )
          );
        })
        .map((obj) => {
          return { id: obj.id, expandedPartners: obj.expandedPartners };
        }) ?? [];

    const selectedUser = getUserDetailsFromId(selectedUserId);

    // Get the dwKeys from the filtered hierarchies to determine which country we're in
    const availableHierarchyDwKeys = hierarchies?.map((h) => h.dwKey) ?? [];

    // Filter existing subscriptions to only include those in the current country (matching hierarchies)
    const existingSubscriptions: string[] =
      selectedUser?.businessPartners
        ?.filter((bp) =>
          bp.services.some((s) => (subscriptionType === 'CO2' ? s.co2Impact : s.csrdPremium)),
        )
        .map((partner) => partner.id)
        .filter((partnerId) => {
          // Only include subscriptions for partners that exist in the filtered hierarchies (current country)
          return availableHierarchyDwKeys.some((dwKey) => dwKey === partnerId);
        }) ?? [];

    setSelectedUserSubscriptions(existingSubscriptions);

    const accessiblePartners = allPartners
      .filter((partner) => {
        if (selectedUser?.businessPartners?.some((userPartner) => userPartner.id === partner.id)) {
          return true;
        }
        if (
          partner.expandedPartners?.some((expandedPartner) =>
            selectedUser?.businessPartners?.some(
              (userPartner) => userPartner.id === expandedPartner,
            ),
          )
        ) {
          return true;
        }

        return false;
      })
      .map((partner) => partner.id);

    return (
      hierarchies
        ?.filter((hierarchy) => accessiblePartners.includes(hierarchy.dwKey))
        .filter((hierarchy) => !existingSubscriptions.includes(hierarchy.dwKey)) ?? []
    );
  }, [selectedUserId, profile, hierarchies, getUserDetailsFromId, subscriptionType]);

  useEffect(() => {
    const notAllowedToPurchaseSubscription =
      selectedUserSubscriptions.length === 0 &&
      selectableHierarchies.length === 0 &&
      !!selectedUserId;

    setNotAllowedToPurchaseSubscription(notAllowedToPurchaseSubscription);
  }, [selectedUserSubscriptions, selectableHierarchies, selectedUserId]);

  const getSelectedHierarchyDetailsFromDwKey: HierarchyDetailsFromDwKey = useCallback(
    (dwKeys: string[] | undefined) => {
      if (!dwKeys || dwKeys.length === 0) return undefined;
      const hierarchy = hierarchies?.find((hierarchy) => hierarchy.dwKey === dwKeys[0]);

      if (!hierarchy) return undefined;
      const childCount =
        hierarchy.childCount > 0
          ? ` +${hierarchy.childCount} ${t('co2_impact.checkout.step1.locations')}`
          : '';

      return { name: hierarchy.name, description: hierarchy.description, childCount: childCount };
    },
    [hierarchies, t],
  );

  if (loading) return <LoadingSelectUserAndHierarchy />;

  return (
    <BoxContainer>
      <SpaceWrapper>
        {!hierarchies && (
          <div style={{ margin: `${spacing.xxsmall} 0 ${spacing.mediumLow} 0` }}>
            <Message
              severity="error"
              title={t('co2_impact.checkout.errors.no.valid.hierarchies.title')}
              description={t('co2_impact.checkout.errors.no.valid.hierarchies.description')}
            />
          </div>
        )}
        {hasEnterprisePartner && subscriptionType === 'CSRD_PREMIUM' && (
          <div style={{ margin: `${spacing.xxsmall} 0 ${spacing.mediumLow} 0` }}>
            <Message severity="info" description={t('csrd.checkout.enterprise.description')} />
          </div>
        )}
        <ForMe
          active={orderForMe}
          selectedHierarchy={innerHierarchy}
          getSelectedHierarchyDetailsFromDwKey={getSelectedHierarchyDetailsFromDwKey}
          handleSelectHierarchy={handleSelectHierarchy}
          handleSelectUsersCategory={handleSelectUsersCategory}
          selectedUserId={selectedUserId}
          getUserDetailsFromId={getUserDetailsFromId}
          hierarchies={selectableHierarchies}
          disabled={!hierarchies || notAllowedToPurchaseSubscription}
          hasAvailableCostCenters={hasAvailableCostCenters}
          allowedToPurchaseSubscription={!notAllowedToPurchaseSubscription}
          selectedUserSubscriptions={selectedUserSubscriptions}
          subscriptionType={subscriptionType}
        />
        {filteredUsers && filteredUsers.length > 0 ? (
          <>
            <Divider />
            <ForSomeoneElse
              active={!orderForMe}
              selectedHierarchy={innerHierarchy}
              getSelectedHierarchyDetailsFromDwKey={getSelectedHierarchyDetailsFromDwKey}
              handleSelectHierarchy={handleSelectHierarchy}
              handleSelectUsersCategory={handleSelectUsersCategory}
              selectedUserId={selectedUserId}
              users={filteredUsers}
              usersLoading={usersLoading}
              handleSelectUser={handleSelectUser}
              getUserDetailsFromId={getUserDetailsFromId}
              hierarchies={selectableHierarchies}
              disabled={!hierarchies || notAllowedToPurchaseSubscription}
              hasAvailableCostCenters={hasAvailableCostCenters}
              allowedToPurchaseSubscription={!notAllowedToPurchaseSubscription}
              selectedUserSubscriptions={selectedUserSubscriptions}
              subscriptionType={subscriptionType}
            />
          </>
        ) : null}
      </SpaceWrapper>
    </BoxContainer>
  );
};

const LoadingSelectUserAndHierarchy = () => (
  <BoxContainer>
    <SpaceWrapper>
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <SkeletonLoader height={25} width={25} mr={15} />{' '}
          <SkeletonLoader height={20} width={250} />
        </div>
        <SkeletonLoader height={18} width={280} ml={40} mb={20} />
        <Container outline style={{ marginTop: '15px' }}>
          <SkeletonLoader height={22} width={220} />
          <SkeletonLoader height={22} width={280} />
          <SkeletonLoader height={22} width={180} />
        </Container>
      </div>
      <Divider />
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <SkeletonLoader height={25} width={25} mr={15} />{' '}
          <SkeletonLoader height={20} width={250} />
        </div>
        <SkeletonLoader height={18} width={280} ml={40} />
      </div>
    </SpaceWrapper>
  </BoxContainer>
);

export default SelectUserAndHierarchy;
